import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppUser, GraphUser } from '../domain/user.model';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { PreviousRouteService } from '../services/previous-url-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { AuthorityEnum } from './authority-enum.model';
import {
  DashboardLibError,
  DashboardRuntimeConfigService
} from 'dashboard-lib';
import { ToastService } from '../services/toast.service';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private _router = inject(Router);
  private previousRouteService = inject(PreviousRouteService);
  private readonly dashboardConfig = inject(DashboardRuntimeConfigService);
  private toastService: ToastService = inject(ToastService);
  private msalService = inject(MsalService);

  graphProfile: GraphUser | undefined;
  roles: BehaviorSubject<string[]> = new BehaviorSubject(['']);
  private destroyRef: DestroyRef = inject(DestroyRef);

  setAppAuthRoles() {
    this.http
      .get(environment.apiConfig.uri)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        this.graphProfile = profile as GraphUser;
        this.http
          .get(`api/users/${this.graphProfile.userPrincipalName}`)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value) => {
            const appUser: AppUser = value as AppUser;

            this.roles.next(appUser.roles.map((role) => role.name));
            localStorage.setItem('roles', JSON.stringify(appUser.roles));

            const roles: string[] = appUser?.roles?.map((r) => r.name) ?? [];
            this.dashboardConfig.setConfig({
              apiBaseUrl: '/api/dashboard/',
              adminRole: ['DEVELOPER'],
              authProvider: {
                isLoggedIn: () => {
                  return this.msalService.instance.getAllAccounts().length > 0;
                },

                hasRole: (role: string) => roles.includes(role),

                logout: (): void => {
                  this.msalService.logoutRedirect();
                },

                getAccessToken: async () => {
                  const account =
                    this.msalService.instance.getActiveAccount() ??
                    this.msalService.instance.getAllAccounts()[0];

                  if (!account) return null;

                  const result =
                    await this.msalService.instance.acquireTokenSilent({
                      account,
                      scopes: environment.apiConfig.scopes
                    });

                  return result.accessToken;
                }
              },
              errorHandler: (error: DashboardLibError): void => {
                this.toastService.showToast(error.message, 'error');
              },
              showAVSettings: false,
              gridWidth: 60,
              gridMaxRows: 2000
            });

            const url = this.previousRouteService.getPreviousUrl();
            // todo previous url handling
            if (this.roles.value.includes(AuthorityEnum.PURCHASE)) {
              //value.authorities.includes(AuthorityEnum.PURCHASE)){
              this._router.navigate(['app/purchase-products']);
            }
          });
      });
  }

  clearStoredRoles(): void {
    localStorage.removeItem('roles');
  }

  getStoredRoles(): string[] {
    const storedRoles: string | null = localStorage.getItem('roles');
    if (storedRoles) {
      const parsedRoles = JSON.parse(storedRoles);
      return parsedRoles.map((role: { name: string }) => role.name);
    }
    return [];
  }

  setAppAuthRolesAndGetRoles(): Observable<string[]> {
    return this.http.get(environment.apiConfig.uri).pipe(
      switchMap((profile) => {
        this.graphProfile = profile as GraphUser;
        return this.http.get(
          `api/users/${this.graphProfile.userPrincipalName}`
        );
      }),
      map((userData) => {
        const appUser = userData as AppUser;
        const roleNames = appUser.roles.map((r) => r.name);
        this.roles.next(roleNames);
        localStorage.setItem('roles', JSON.stringify(appUser.roles));
        return roleNames;
      })
    );
  }
}
